import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE, authHeaders } from '../../utils/Utils.js';
import ErrorMessage from '../../shared/ErrorMessage.js';
import DateInput from '../../shared/inputs/DateInput.js';
import LotProjectField from '../../shared/lots/LotProjectField.js';
import '../../shared/DataCollectionAdd.css';

export const AddLot = ({ protocols, bulls, defaultProtocolId, onSuccess }) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const [name, setName] = useState("");
  const [selectedProtocolId, setSelectedProtocolId] = useState("");
  const [selectedBullId, setSelectedBullId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [projectIds, setProjectIds] = useState([]);

  // Pre-select default protocol when it becomes available
  useEffect(() => {
    if (defaultProtocolId && !selectedProtocolId) {
      setSelectedProtocolId(defaultProtocolId);
    }
  }, [defaultProtocolId, selectedProtocolId]);

  // Set default start date to today
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!selectedProtocolId) {
      setErrorMessage(t("select_protocol_error"));
      return;
    }

    const selectedProtocol = protocols.find(p => p._id === selectedProtocolId);
    if (!selectedProtocol) {
      setErrorMessage(t("select_protocol_error"));
      return;
    }

    const selectedBull = bulls?.find(b => b._id === selectedBullId);
    const body = JSON.stringify({
      type: 'reproduction',
      name: name.trim(),
      protocol_id: selectedProtocolId,
      start_date: startDate,
      bull_id: selectedBullId || null,
      project_ids: projectIds,
    });

    fetch(`${API_BASE}/api/data/lots`, {
      method: 'POST',
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: body,
    })
    .then(response => {
      if (response.ok) {
        return response.json().then(lot => onSuccess(lot));
      }
      if (response.status === 401) {
        localStorage.removeItem('zapmanejo_token');
        localStorage.removeItem('zapmanejo_user');
        window.location.reload();
      }
      return response.text().then(text => {
        throw new Error(text);
      });
    })
    .catch(error => {
      console.error('Error creating lot:', error);
      setErrorMessage(t("error_creating_lot"));
    });
  };

  return (
    <form className="DataCollectionAdd" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="name">{t("repro_lot_name")}</label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("repro_lot_name_placeholder")}
        />
      </div>

      <div className="form-group">
        <label htmlFor="protocol">{t("protocol_name")}</label>
        <select
          id="protocol"
          name="protocol"
          value={selectedProtocolId}
          onChange={(e) => setSelectedProtocolId(e.target.value)}
          required
        >
          <option value="">-- {t("select_protocol")} --</option>
          {protocols.map((protocol) => (
            <option key={protocol._id} value={protocol._id}>
              {protocol.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="start_date">{t("start_date")}</label>
        <DateInput
          id="start_date"
          name="start_date"
          value={startDate}
          onChange={(v) => setStartDate(v)}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="bull">{t("bull")}</label>
        <select
          id="bull"
          name="bull"
          value={selectedBullId}
          onChange={(e) => setSelectedBullId(e.target.value)}
        >
          <option value="">-- {t("select_bull")} --</option>
          {bulls?.map((bull) => (
            <option key={bull._id} value={bull._id}>
              {bull.name}
            </option>
          ))}
        </select>
      </div>

      <LotProjectField value={projectIds} onChange={setProjectIds} groupClassName="form-group" />

      <button type="submit">{t("add_button")}</button>
      <ErrorMessage message={errorMessage} />
    </form>
  );
};

export default AddLot;
